/**
 * Notification Configuration
 * Central configuration for notification navigation across all apps
 */

/**
 * App URLs Configuration
 * Environment-based URLs for different apps in the ecosystem
 */
export const APP_URLS = {
  // Main Application (language-village)
  MAIN: {
    PRODUCTION: process.env.REACT_APP_FRONTEND_URL || 'http://localhost:3000',
    LOCAL: 'http://localhost:3000',
  },
  
  // Live Streaming Application (language-village-stream)
  STREAM: {
    PRODUCTION: process.env.REACT_APP_FRONTEND_STREAM_URL || 'http://localhost:3001',
    LOCAL: 'http://localhost:3001',
  },
  
  // Exams Application (language-village-exams)
  EXAMS: {
    PRODUCTION: process.env.REACT_APP_FRONTEND_EXAM_URL || 'http://localhost:3002',
    LOCAL: 'http://localhost:3002',
  },
};

/**
 * Get the appropriate URL based on environment
 * @param {string} app - App type: 'MAIN', 'STREAM', 'EXAMS'
 * @returns {string} - Base URL for the app
 */
export const getAppUrl = (app) => {
  const isDevelopment = process.env.NODE_ENV === 'development' || 
                        !process.env.NODE_ENV || 
                        window.location.hostname === 'localhost';
  
  return isDevelopment ? APP_URLS[app].LOCAL : APP_URLS[app].PRODUCTION;
};

/**
 * Notification Type Constants
 * Standardized notification types across the system
 */
export const NOTIFICATION_TYPES = {
  // Exam Notifications
  EXAM_PUBLISHED: 'exam_published',
  EXAM_STARTED: 'exam_started',
  EXAM_DEADLINE_REMINDER: 'exam_deadline_reminder',
  EXAM_GRADED: 'exam_graded',
  EXAM_SUBMITTED: 'exam_submitted',
  GRADING_REQUIRED: 'grading_required',
  COURSE_COMPLETED: 'course_completed',
  
  // Live Session Notifications
  LIVE_STARTED: 'live_started',
  LIVE_SCHEDULED: 'live_scheduled',
  LIVE_ENDED: 'live_ended',
  
  // Course Notifications
  COURSE_ACTIVATED: 'course_activated',
  COURSE_INSTRUCTOR_ASSIGNED: 'course_instructor_assigned',
  LESSON_ACTIVATED: 'lesson_activated',
  
  // Payment Notifications
  PAYMENT_VERIFIED: 'payment_verified',
  PAYMENT_REMINDER: 'payment_reminder',
  
  // General Notifications
  GENERAL: 'general',
};

/**
 * Navigation Routes Configuration
 * Defines route patterns for each notification type
 */
/**
 * Helper function to extract exam code from backend URLs
 * @param {Object} options - Notification options from backend
 * @returns {string|null} - Extracted exam code or null
 */
const extractExamCode = (options) => {
  // If exam_code is directly provided, use it
  if (options.exam_code) {
    return options.exam_code;
  }

  // Try to extract from take_exam_url: "/exam/ABCB-C6NQ-7477-FVLY/take/1"
  if (options.take_exam_url) {
    const match = options.take_exam_url.match(/\/exam\/([A-Z0-9\-]+)/);
    if (match && match[1]) {
      return match[1];
    }
  }

  // Try to extract from exam_url: "/exam/ABCB-C6NQ-7477-FVLY"
  if (options.exam_url) {
    const match = options.exam_url.match(/\/exam\/([A-Z0-9\-]+)/);
    if (match && match[1]) {
      return match[1];
    }
  }

  // Try to extract from results_url
  if (options.results_url) {
    const match = options.results_url.match(/\/exam\/([A-Z0-9\-]+)/);
    if (match && match[1]) {
      return match[1];
    }
  }

  console.warn('⚠️ Could not extract exam_code from notification options:', options);
  return null;
};

export const NOTIFICATION_ROUTES = {
  // Exam Routes (External - Exams App)
  [NOTIFICATION_TYPES.EXAM_PUBLISHED]: {
    app: 'EXAMS',
    getRoute: (options) => {
      const examCode = extractExamCode(options);
      return `/exam/${examCode || 'unknown'}`;
    },
    external: true,
  },
  [NOTIFICATION_TYPES.EXAM_STARTED]: {
    app: 'EXAMS',
    getRoute: (options) => {
      const examCode = extractExamCode(options);
      return `/exam/${examCode || 'unknown'}/take/${options.exam_id || ''}`;
    },
    external: true,
  },
  [NOTIFICATION_TYPES.EXAM_DEADLINE_REMINDER]: {
    app: 'EXAMS',
    getRoute: (options) => {
      const examCode = extractExamCode(options);
      return `/exam/${examCode || 'unknown'}`;
    },
    external: true,
  },
  [NOTIFICATION_TYPES.EXAM_GRADED]: {
    app: 'EXAMS',
    getRoute: (options) => {
      const examCode = extractExamCode(options);
      return `/exam/${examCode || 'unknown'}/results`;
    },
    external: true,
  },
  [NOTIFICATION_TYPES.EXAM_SUBMITTED]: {
    app: 'EXAMS',
    getRoute: (options) => {
      const examCode = extractExamCode(options);
      return `/exam/${examCode || 'unknown'}/results`;
    },
    external: true,
  },
  [NOTIFICATION_TYPES.GRADING_REQUIRED]: {
    app: 'EXAMS',
    getRoute: (options) => `/correction/${options.attempt_id}`,
    external: true,
  },
  [NOTIFICATION_TYPES.COURSE_COMPLETED]: {
    app: 'EXAMS',
    getRoute: (options) => `/scores`,
    external: true,
  },
  
  // Live Session Routes (External - Stream App)
  [NOTIFICATION_TYPES.LIVE_STARTED]: {
    app: 'STREAM',
    getRoute: (options) => {
      // If live_link is provided with full URL, extract the path
      if (options.live_link) {
        try {
          const url = new URL(options.live_link);
          return url.pathname + url.search; // e.g., "/oys-jcfm-vqt?lesson=1"
        } catch (e) {
          // If it's already a path (not full URL), use it as-is
          return options.live_link;
        }
      }
      
      // Fallback: construct from meeting_code and model_id/lesson_id
      const lessonId = options.lesson_id || options.model_id || '';
      return `/${options.meeting_code}?lesson=${lessonId}`;
    },
    external: true,
  },
  
  // Course Routes (Internal - Main App)
  [NOTIFICATION_TYPES.COURSE_ACTIVATED]: {
    app: 'MAIN',
    getRoute: (options) => `/courses/${options.course_id}`,
    external: false,
  },
  [NOTIFICATION_TYPES.LESSON_ACTIVATED]: {
    app: 'MAIN',
    getRoute: (options) => `/courses/${options.course_id}`,
    external: false,
  },
  [NOTIFICATION_TYPES.PAYMENT_VERIFIED]: {
    app: 'MAIN',
    getRoute: (options) => `/courses/${options.course_id}`,
    external: false,
  },
};

/**
 * UI Configuration for notification types
 * Defines visual appearance and behavior for each notification type
 */
export const NOTIFICATION_UI_CONFIG = {
  [NOTIFICATION_TYPES.EXAM_PUBLISHED]: {
    icon: '📋',
    actionText: 'View Exam',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    badgeColor: 'bg-blue-100 text-blue-700',
    badgeText: 'New Exam',
    isUrgent: false,
  },
  [NOTIFICATION_TYPES.EXAM_STARTED]: {
    icon: '🔴',
    actionText: 'Take Exam Now',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-300',
    badgeColor: 'bg-red-100 text-red-700',
    badgeText: 'URGENT',
    isUrgent: true,
    animate: true,
  },
  [NOTIFICATION_TYPES.EXAM_DEADLINE_REMINDER]: {
    icon: '⏰',
    actionText: 'Take Exam',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    badgeColor: 'bg-yellow-100 text-yellow-700',
    badgeText: 'Deadline Soon',
    isUrgent: true,
  },
  [NOTIFICATION_TYPES.EXAM_GRADED]: {
    icon: '📊',
    actionText: 'View Results',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    badgeColor: 'bg-green-100 text-green-700',
    badgeText: 'Results Ready',
    isUrgent: false,
  },
  [NOTIFICATION_TYPES.EXAM_SUBMITTED]: {
    icon: '✅',
    actionText: 'View Submission',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    badgeColor: 'bg-green-100 text-green-700',
    badgeText: 'Submitted',
    isUrgent: false,
  },
  [NOTIFICATION_TYPES.GRADING_REQUIRED]: {
    icon: '📝',
    actionText: 'Grade Now',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    badgeColor: 'bg-purple-100 text-purple-700',
    badgeText: 'Grading',
    isUrgent: false,
  },
  [NOTIFICATION_TYPES.COURSE_COMPLETED]: {
    icon: '🎓',
    actionText: 'View Report',
    bgColor: 'bg-indigo-50',
    borderColor: 'border-indigo-200',
    badgeColor: 'bg-indigo-100 text-indigo-700',
    badgeText: 'Completed',
    isUrgent: false,
  },
  [NOTIFICATION_TYPES.LIVE_STARTED]: {
    icon: '🎥',
    actionText: 'Join Live',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-300',
    badgeColor: 'bg-red-100 text-red-700',
    badgeText: 'LIVE',
    isUrgent: true,
    animate: true,
  },
  [NOTIFICATION_TYPES.COURSE_ACTIVATED]: {
    icon: '📚',
    actionText: 'View Course',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    badgeColor: 'bg-blue-100 text-blue-700',
    badgeText: 'Activated',
    isUrgent: false,
  },
  [NOTIFICATION_TYPES.LESSON_ACTIVATED]: {
    icon: '📖',
    actionText: 'View Lesson',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    badgeColor: 'bg-blue-100 text-blue-700',
    badgeText: 'New Lesson',
    isUrgent: false,
  },
  [NOTIFICATION_TYPES.PAYMENT_VERIFIED]: {
    icon: '💳',
    actionText: 'View Course',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    badgeColor: 'bg-green-100 text-green-700',
    badgeText: 'Payment Verified',
    isUrgent: false,
  },
  // Default/Fallback
  [NOTIFICATION_TYPES.GENERAL]: {
    icon: '🔔',
    actionText: 'View',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
    badgeColor: 'bg-gray-100 text-gray-700',
    badgeText: 'Notification',
    isUrgent: false,
  },
};

/**
 * Get UI configuration for a notification type
 * @param {string} notificationType - Type of notification
 * @returns {Object} - UI configuration object
 */
export const getNotificationUIConfig = (notificationType) => {
  return NOTIFICATION_UI_CONFIG[notificationType] || NOTIFICATION_UI_CONFIG[NOTIFICATION_TYPES.GENERAL];
};
